import React, {ReactElement, useEffect, useState} from 'react'
import Header from '../../components/header/Header'
import Footer from '../../components/footer/Footer'
import Container from '../../components/container/Container'
import styles from './LoginLayout.module.scss'
import {ReactComponent as DCLogo} from '../../assets/images/dc-logo-blue.svg'
import {ReactComponent as DCLogoSquare} from '../../assets/images/dc-logo-square.svg'
import {useTranslation} from 'react-i18next'
import {localeData} from '../../components/footer/Footer.types'
import Cookies from 'js-cookie'
import {getLocales} from 'utils/HttpUrlConstants'
import {ACCOUNTS_LOCALE_COOKIE_NAME, NEW_LOOK_ANNOUNCEMENT_DISMISSED} from '../../utils/constants'
import {AnimationTypes, WaveBg} from '../../components/wave-bg'
import {LoginLayoutProps} from './LoginLayout.types'
import cn from 'classnames'
import {Outlet, useLocation} from 'react-router-dom'
import {getItemFromLS, setItemToLS} from '../../utils/LocalStorage'
import {IToastNotification, ToasterContainer, ToasterMessageType} from '@digicert/dcone-common-ui'
import {login, password, twoFactorSetup} from '../../utils/routes'

const LogInLayout = (
  {
    animationType,
    animationStartDirection,
    className,
    contentMaxWidth
  }: LoginLayoutProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [locales, setLocales] = useState<localeData[]>()
  const [, languageCode] = useState('en-US')
  const [language, setLanguage] = useState('English')
  const NEW_LOOK_NOTIFICATION_ID = 'new-look-notification'
  const [notifications, setNotifications] = useState<IToastNotification[]>([
    {
      type: ToasterMessageType.ANNOUNCEMENT,
      title: t('notifications.newLook.title'),
      description: t('notifications.newLook.description'),
      id: 'new-look-notification'
    }
  ])
  const location = useLocation()
  const [animation, setAnimation] = useState(animationType)
  const [animationDirection, setAnimationDirection] = useState(animationStartDirection)
  const [contentWidth, setContentWidth] = useState(contentMaxWidth)

  const hideNotification = (id: string | number) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
    if(id === NEW_LOOK_NOTIFICATION_ID) {
      setItemToLS(NEW_LOOK_ANNOUNCEMENT_DISMISSED, 'true')
    }
  }

  // update language, code and cookie on change
  const onLocaleChange = (language: string, code: string) => {
    i18n.changeLanguage(code).then(() => {
      languageCode(language)
      setLanguage(code)
      Cookies.set(ACCOUNTS_LOCALE_COOKIE_NAME, code.replace('-', '_'), { secure: true })
    })
  }

  // fetch locales and reformat response for compatibility
  useEffect(() => {
    fetch(getLocales(), {
      method: 'get'
    }).then(response => response.json())
    .then(data => {
      const localeData = data.map(locale => {
        const code = locale.code.replace('_', '-')
        return {
          key: code,
          label: locale.language,
          onClick: () => onLocaleChange(locale.language, code)
        }
      })
      setLocales(localeData)
    });
  }, [])

  // if cookie lang set use as default
  useEffect(() => {
    const langCode = Cookies.get(ACCOUNTS_LOCALE_COOKIE_NAME)
    if(locales && langCode) {
      const selectedLanguage = locales.find(locale => locale.key === langCode.replace('_', '-'))
      if (selectedLanguage) {
        i18n.changeLanguage(selectedLanguage.key.replace('_', '-')).then(()=> {
          setLanguage(selectedLanguage.label as string)
        })
      }
    }
  }, [locales, language]);

  // some routes have specific animations and custom content sizes set on location change
  useEffect(() => {
    switch (location.pathname) {
      case login:
        setAnimation(AnimationTypes.WAVE_RIGHT)
        setAnimationDirection('left')
        break;
      case password:
        setAnimation(AnimationTypes.WAVE_LEFT)
        setAnimationDirection('right')
        break;
      case twoFactorSetup:
        setContentWidth('auto')
        break;
    }
  }, [location, animation, animationDirection, contentWidth]);

  if (locales) {
    return (
      <>
        <div className={styles.branding}>
          <DCLogo/>
        </div>
        <WaveBg animationType={animation} animationStartDirection={animationDirection}/>
        <section className={cn(styles.loginLayout, className)}>
          <Container maxWidth='696px' centered className={styles.container}>
            <Header logo={<DCLogoSquare/>}/>
            <div className={styles.content} style={{maxWidth: contentWidth ? contentWidth : '420px'}}>
              <Outlet />
            </div>
            <Footer locales={locales} currentLanguage={language}/>
          </Container>
        </section>
        {!getItemFromLS(NEW_LOOK_ANNOUNCEMENT_DISMISSED) && (
          <ToasterContainer className='toast-container' hideNotification={hideNotification} notifications={notifications}/>
        )}
      </>
    )
  }

  else return <></>
}

export default LogInLayout
